import { runBudgetOptimisation } from '@/dashboards/BudgetOptimization/api';
import { readOptimizerMessages } from '@/dashboards/shared/optimisation/optimiserRun';
import {
    deriveFlags,
    initialSessionState,
    reconcileInputs,
    sessionReducer,
    type ChannelInput,
    type OptimisationInputs,
    type OptimisationRun,
    type SessionFlags,
} from '@/dashboards/shared/optimisation/optimisationSession';
import { withoutColdStartNoise } from '@/dashboards/shared/optimisation/optimiserMessages';
import { toError } from '@/dashboards/shared/optimisation/toError';
import type { DashboardRule, OptimisationBase } from '@/optimizer';
import type { OptimizerServiceManagerReturn } from '@/optimizer/createOptimizerServiceManager';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
    detectZeroInvestmentAdjustment,
    resolveBootstrapTarget,
    type ZeroInvestmentAdjustmentInfo,
} from './budgetSessionRules';

export type BudgetSearch = {
    budget?: number;
    optimizationBase?: OptimisationBase;
};

export type CachedOptimizationSpends = {
    optimalSpends: Record<string, number>;
    budget: number | null;
} | null;

export type UseBudgetOptimisationSessionArgs = {
    configRules: DashboardRule[];
    optimizer: Pick<
        OptimizerServiceManagerReturn,
        'getDefaultBudget' | 'messages'
    >;
    optimizerReady: boolean;
    // Read once, at bootstrap. Afterwards the URL only mirrors the committed run.
    search: BudgetSearch;
    setSearch: (patch: BudgetSearch) => unknown;
    startDate: string;
    endDate: string;
    variableNameMap: Record<string, string>;
    onZeroInvestmentAdjustment?: (info: ZeroInvestmentAdjustmentInfo) => void;
    cachedOptimization?: CachedOptimizationSpends;
};

export type UseBudgetOptimisationSessionReturn = SessionFlags & {
    draft: OptimisationInputs | null;
    committed: OptimisationRun | null;
    error: Error | null;
    setTarget: (
        optimizationBase: OptimisationBase,
        budget: number | undefined,
    ) => void;
    setChannel: (channel: string, value: ChannelInput) => void;
    resetDraft: () => void;
    dismissError: () => void;
    runOptimization: () => void;
};

const applySearch = (
    setSearch: UseBudgetOptimisationSessionArgs['setSearch'],
    patch: BudgetSearch,
) => {
    Promise.resolve(setSearch(patch)).catch(() => undefined);
};

// Single owner of the yearly optimisation: one draft the control panel edits, one committed
// run the page renders, and exactly one optimiser call per Run. Ported from the campaign
// session (CampaignOptimization/core/useOptimizationSessionState).
export const useBudgetOptimisationSession = (
    args: UseBudgetOptimisationSessionArgs,
): UseBudgetOptimisationSessionReturn => {
    const [state, dispatch] = useReducer(sessionReducer, initialSessionState);

    // Latest-props mirror for async callbacks; assigned during render, read only inside callbacks/effects.
    const latest = useRef(args);
    latest.current = args;
    const stateRef = useRef(state);
    stateRef.current = state;

    const runningRef = useRef(false);
    const generationRef = useRef(0);
    const bootstrappedRef = useRef(false);

    const startRun = useCallback(async (inputs: OptimisationInputs) => {
        if (runningRef.current) return;
        runningRef.current = true;
        const generation = ++generationRef.current;
        const previous = stateRef.current.committed;
        dispatch({ type: 'RUN_STARTED', generation });

        const { configRules, optimizer } = latest.current;
        try {
            await readOptimizerMessages(optimizer);
            const result = await runBudgetOptimisation({
                inputs,
                rules: configRules,
            });
            const messages = withoutColdStartNoise(
                await readOptimizerMessages(optimizer),
                { isFirstRun: generation === 1 },
            );
            const run: OptimisationRun = {
                inputs: reconcileInputs(inputs, result),
                result,
                messages,
                finishedAt: Date.now(),
            };
            dispatch({ type: 'RUN_SUCCEEDED', generation, run });

            const { setSearch, variableNameMap, onZeroInvestmentAdjustment } =
                latest.current;
            applySearch(setSearch, {
                budget: run.inputs.budget,
                optimizationBase: run.inputs.optimizationBase,
            });
            const adjustment = detectZeroInvestmentAdjustment({
                previous,
                requested: inputs,
                run,
                variableNameMap,
            });
            if (adjustment) onZeroInvestmentAdjustment?.(adjustment);
        } catch (error) {
            // The modal shows a one-line summary; keep the full worker payload in DevTools.
            console.error(
                '[budget-optimisation] optimisation run failed',
                error,
            );
            dispatch({ type: 'RUN_FAILED', generation, error: toError(error) });
        } finally {
            runningRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!args.optimizerReady || bootstrappedRef.current) return;
        bootstrappedRef.current = true;

        void (async () => {
            try {
                const { optimizer, search, startDate, endDate, cachedOptimization } =
                    latest.current;
                const target = resolveBootstrapTarget(search);

                const cachedBudget = cachedOptimization?.budget;
                const budget =
                    target.budget ?? cachedBudget ?? (await optimizer.getDefaultBudget());

                const channels: Record<string, ChannelInput> = {};
                if (cachedOptimization?.optimalSpends) {
                    for (const [channel, spend] of Object.entries(cachedOptimization.optimalSpends)) {
                        channels[channel] = { locked: true, investment: Math.round(spend) };
                    }
                }

                const inputs: OptimisationInputs = {
                    budget,
                    optimizationBase: target.optimizationBase,
                    startDate,
                    endDate,
                    channels,
                };
                dispatch({ type: 'BOOTSTRAPPED', inputs });
                await startRun(inputs);
            } catch (error) {
                dispatch({ type: 'BOOTSTRAP_FAILED', error: toError(error) });
            }
        })();
    }, [args.optimizerReady, startRun]);

    const runOptimization = useCallback(() => {
        const draft = stateRef.current.draft;
        if (draft) void startRun(draft);
    }, [startRun]);

    const setTarget = useCallback(
        (optimizationBase: OptimisationBase, budget: number | undefined) =>
            dispatch({
                type: 'EDIT',
                patch: { optimizationBase, budget: budget ?? 0 },
            }),
        [],
    );
    const setChannel = useCallback(
        (channel: string, value: ChannelInput) =>
            dispatch({ type: 'SET_CHANNEL', channel, value }),
        [],
    );
    const resetDraft = useCallback(() => dispatch({ type: 'RESET_DRAFT' }), []);
    const dismissError = useCallback(
        () => dispatch({ type: 'DISMISS_ERROR' }),
        [],
    );

    // Budget optimisation has no read-only state; edit rights are enforced per row by CASL.
    const flags = useMemo(() => deriveFlags(state, true), [state]);

    return useMemo<UseBudgetOptimisationSessionReturn>(
        () => ({
            ...flags,
            draft: state.draft,
            committed: state.committed,
            error: state.error,
            setTarget,
            setChannel,
            resetDraft,
            dismissError,
            runOptimization,
        }),
        [
            flags,
            state.draft,
            state.committed,
            state.error,
            setTarget,
            setChannel,
            resetDraft,
            dismissError,
            runOptimization,
        ],
    );
};
